import React, { useEffect } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useBlocker } from 'react-router-dom'
import {
  Button,
  DatePicker,
  Flex,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  theme,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { countries } from '../../../../libs/countries'
import {
  $verificationRequestV2,
  getVerificationV2,
  setFormDirtyStateV2,
  setIsFormDirtyV2,
  submitEditVerificationRequestV2,
  updateVerificationRequestV2,
} from '../../edit-v2/model'
import { useUnit } from 'effector-react'
import { useAutoCheck } from '../DocumentAutoCheck/useAutoCheck'
import {
  AutoCheckHint,
  isFieldMismatch,
} from '../DocumentAutoCheck/AutoCheckHint'
import { AutoCheckBanner } from '../DocumentAutoCheck/AutoCheckBanner'
import {
  CompareFieldKey,
  CompareRow,
  valueForForm,
} from '../DocumentAutoCheck/compare'

type FormValues = {
  firstName: string
  middleName: string
  lastName: string
  country: string
  dateOfBirth: Dayjs | null
  documentNumber: string
  expirationDate: Dayjs | null
}

type Props = {
  defaultValues?: {
    firstName?: string
    middleName?: string
    lastName?: string
    country?: string
    dateOfBirth?: string
    documentNumber?: string
    expirationDate?: string
  }
  disabled?: boolean
}

const countryOptions = countries.map(
  (item: { value: string; text: string }) => ({
    value: item.value,
    label: item.text,
  }),
)

export const VerificationEditForm = ({ defaultValues, disabled }: Props) => {
  const { token } = theme.useToken()
  const verificationRequest = useUnit($verificationRequestV2)
  const loading = useUnit(updateVerificationRequestV2.pending)
  const fetching = useUnit(getVerificationV2.pending)
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty, dirtyFields },
  } = useForm<FormValues>({
    mode: 'onChange',
  })

  const blocker = useBlocker(() => isDirty)

  useEffect(() => {
    setIsFormDirtyV2(isDirty)
  }, [isDirty])

  const formValues = useWatch({ control }) as FormValues
  const watchedExpirationDate = formValues.expirationDate
  const dirtyFieldsKey = Object.keys(dirtyFields)
    .filter((k) => Boolean((dirtyFields as Record<string, boolean>)[k]))
    .sort()
    .join(',')

  useEffect(() => {
    setFormDirtyStateV2({
      dirtyFieldKeys: dirtyFieldsKey ? dirtyFieldsKey.split(',') : [],
      expirationDate:
        watchedExpirationDate && watchedExpirationDate.isValid()
          ? watchedExpirationDate.format('YYYY-MM-DD')
          : '',
      values: {
        firstName: formValues.firstName ?? '',
        middleName: formValues.middleName ?? '',
        lastName: formValues.lastName ?? '',
        country: formValues.country ?? '',
        dateOfBirth: formValues.dateOfBirth?.isValid()
          ? formValues.dateOfBirth.format('YYYY-MM-DD')
          : '',
        documentNumber: formValues.documentNumber ?? '',
        expirationDate: watchedExpirationDate?.isValid()
          ? watchedExpirationDate.format('YYYY-MM-DD')
          : '',
      },
    })
  }, [dirtyFieldsKey, formValues, watchedExpirationDate])

  useEffect(() => {
    if (blocker.state === 'blocked') {
      Modal.confirm({
        title: 'Changes will not be saved',
        content: 'Save the changes you made?',
        okText: 'Save',
        cancelText: 'Cancel',
        onOk: () =>
          handleSubmit((data) => {
            handleFormSubmit(data)
            blocker.proceed()
          })(),
        onCancel: () => blocker.reset(),
      })
    }
  }, [blocker.state])

  useEffect(() => {
    if (!isDirty) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isDirty])

  useEffect(() => {
    if (defaultValues) {
      reset({
        firstName: defaultValues.firstName ?? '',
        middleName: defaultValues.middleName ?? '',
        lastName: defaultValues.lastName ?? '',
        country: defaultValues.country ?? '',
        dateOfBirth: defaultValues.dateOfBirth
          ? dayjs(defaultValues.dateOfBirth)
          : null,
        documentNumber: defaultValues.documentNumber ?? '',
        expirationDate: defaultValues.expirationDate
          ? dayjs(defaultValues.expirationDate)
          : null,
      })
    }
  }, [defaultValues, reset])

  const {
    state: autoCheckState,
    rowByKey,
    appliedKeys,
    markApplied,
  } = useAutoCheck(verificationRequest as Record<string, unknown>)

  const applyFromDoc = (row: CompareRow) => {
    const value = valueForForm(row.key, row.docRawValue)
    if (!value) return
    if (row.key === 'dateOfBirth' || row.key === 'expirationDate') {
      const d = dayjs(value)
      if (!d.isValid()) return
      setValue(row.key, d, { shouldDirty: true })
    } else {
      setValue(row.key, value, { shouldDirty: true })
    }
    markApplied(row.key)
  }

  const fieldStatus = (
    key: CompareFieldKey,
    hasError: boolean,
    isDirtyField: boolean,
  ): 'error' | 'warning' | undefined => {
    if (hasError) return 'error'
    if (isDirtyField) return 'warning'
    if (!appliedKeys.has(key) && isFieldMismatch(rowByKey.get(key)))
      return 'warning'
    return undefined
  }

  const handleCancelChanges = () => {
    Modal.confirm({
      title: 'Discard changes?',
      content:
        'You have unsaved changes. Are you sure you want to discard them?',
      okText: 'Discard',
      cancelText: 'Keep editing',
      okButtonProps: { danger: true },
      onOk: () => reset(),
    })
  }

  const handleFormSubmit = (data: FormValues) => {
    submitEditVerificationRequestV2({
      ...verificationRequest,
      firstName: data.firstName,
      middleName: data.middleName,
      lastName: data.lastName,
      country: data.country,
      dateOfBirth: data.dateOfBirth
        ? data.dateOfBirth.format('YYYY-MM-DD')
        : '',
      documentNumber: data.documentNumber,
      expirationDate: data.expirationDate
        ? data.expirationDate.format('YYYY-MM-DD')
        : '',
    })
  }

  if (fetching) return <Spin style={{ display: 'block', minHeight: 500 }} />

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <AutoCheckBanner state={autoCheckState} />
        <Controller
          name="firstName"
          control={control}
          rules={{ required: 'First name is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>First name</label>
              <Input
                {...field}
                placeholder="First name"
                disabled={disabled}
                status={fieldStatus('firstName', !!error, isDirty)}
              />
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('firstName')}
                applied={appliedKeys.has('firstName')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="middleName"
          control={control}
          render={({ field, fieldState: { isDirty } }) => (
            <div>
              <label>Middle name</label>
              <Input
                {...field}
                placeholder="Middle name"
                disabled={disabled}
                status={fieldStatus('middleName', false, isDirty)}
              />
              <AutoCheckHint
                row={rowByKey.get('middleName')}
                applied={appliedKeys.has('middleName')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="lastName"
          control={control}
          rules={{ required: 'Last name is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>Last name</label>
              <Input
                {...field}
                placeholder="Last name"
                disabled={disabled}
                status={fieldStatus('lastName', !!error, isDirty)}
              />
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('lastName')}
                applied={appliedKeys.has('lastName')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="country"
          control={control}
          rules={{ required: 'Country is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>Country</label>
              <Select
                {...field}
                placeholder="Select country"
                showSearch
                disabled={disabled}
                optionFilterProp="label"
                options={countryOptions}
                status={fieldStatus('country', !!error, isDirty)}
                style={{ width: '100%' }}
              />
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('country')}
                applied={appliedKeys.has('country')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="dateOfBirth"
          control={control}
          rules={{ required: 'Date of birth is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>Date of birth</label>
              <DatePicker
                {...field}
                placeholder="YYYY-MM-DD"
                format={{ type: 'mask', format: 'YYYY-MM-DD' }}
                disabled={disabled}
                status={fieldStatus('dateOfBirth', !!error, isDirty)}
                style={{ width: '100%' }}
                disabledDate={(current) =>
                  current
                    ? current > dayjs().subtract(18, 'year').endOf('day')
                    : false
                }
              />
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('dateOfBirth')}
                applied={appliedKeys.has('dateOfBirth')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="documentNumber"
          control={control}
          rules={{ required: 'Document number is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>Document number</label>
              <Input
                {...field}
                placeholder="Document number"
                disabled={disabled}
                status={fieldStatus('documentNumber', !!error, isDirty)}
              />
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('documentNumber')}
                applied={appliedKeys.has('documentNumber')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        <Controller
          name="expirationDate"
          control={control}
          rules={{ required: 'Expiration date is required' }}
          render={({ field, fieldState: { error, isDirty } }) => (
            <div>
              <label>Expiration date</label>
              <Flex gap={8}>
                <DatePicker
                  {...field}
                  placeholder="YYYY-MM-DD"
                  format={{ type: 'mask', format: 'YYYY-MM-DD' }}
                  disabled={disabled}
                  status={fieldStatus('expirationDate', !!error, isDirty)}
                  style={{ width: '100%' }}
                  disabledDate={(current) =>
                    current ? current <= dayjs().startOf('day') : false
                  }
                />
                <Button
                  disabled={disabled}
                  style={{
                    backgroundColor: token.colorFillTertiary,
                    borderColor: token.colorBorder,
                    color: token.colorText,
                  }}
                  onClick={() =>
                    setValue('expirationDate', dayjs().add(10, 'year'), {
                      shouldDirty: true,
                    })
                  }
                >
                  Life long
                </Button>
              </Flex>
              {error && (
                <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                  {error.message}
                </div>
              )}
              <AutoCheckHint
                row={rowByKey.get('expirationDate')}
                applied={appliedKeys.has('expirationDate')}
                onApply={applyFromDoc}
              />
            </div>
          )}
        />

        {isDirty && (
          <Flex vertical justify="center" gap={12} style={{ marginTop: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={disabled}
            >
              Save
            </Button>
            <Button
              type="text"
              onClick={handleCancelChanges}
              disabled={disabled}
            >
              Cancel Changes
            </Button>
          </Flex>
        )}
      </Space>
    </form>
  )
}
